import { toExcelValue } from '../../column-types';
import { readExportValue } from '../value';
import { applyCellFormat, applySlotFill } from './apply-format';
import type { ExcelWriteContext } from './types';

/**
 * Emit a single data row into the worksheet and stamp the per-column
 * number format + alignment on every cell. Formula cells are built as
 * ExcelJS `{ formula, result }` objects; plain values are routed through
 * `toExcelValue(col, value)` which handles date coercion and duration
 * conversion (minutes → day fractions).
 *
 * The cached `result` is the value already computed by the caller (the
 * accessor that drives the on-screen renderer). Older Excel versions
 * (2013, 2010) trust the cached `<v>` over recomputing the formula on
 * file open, so without this they would display 0 until a manual
 * `Ctrl+Alt+F9`. Modern Excel still re-evaluates the live formula when
 * any input changes, so caching the value does not freeze the column.
 *
 * When `outlineLevel > 0`, the row is tagged with Excel's native outline
 * level so the worksheet renders a `+`/`−` collapse button in the left
 * margin and users can fold groups just like they would in DevExpress
 * grid exports.
 *
 * Returns the 1-based Excel row number so the caller can track the
 * range used by subsequent SUBTOTAL formulas and banded-row styling.
 */
export function writeDataRow<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sheet: any,
  row: T,
  ctx: ExcelWriteContext<T>,
  outlineLevel = 0
): number {
  const rowRef = sheet.rowCount + 1;
  const cells: Record<string, unknown> = {};

  for (const col of ctx.columns) {
    if (col.exportFormula) {
      const formula = col.exportFormula({
        row,
        rowRef,
        col: (id) => ctx.letterByColId.get(id) ?? 'A',
      });
      if (formula !== null) {
        const cached = readExportValue(col, row);
        const result = typeof cached === 'number' ? cached : 0;
        cells[col.id] = { formula, result };
        continue;
      }
    }
    const raw = readExportValue(col, row);
    cells[col.id] = toExcelValue(col, raw);
  }

  const addedRow = sheet.addRow(cells);

  // Stamp numFmt + alignment on every cell of the newly-inserted row.
  // ExcelJS does NOT reliably propagate `sheet.columns[i].style.numFmt`
  // to cells added via `addRow(object)`, so we apply the format here.
  for (const col of ctx.columns) {
    const cell = addedRow.getCell(col.id);
    applyCellFormat(cell, col.id, ctx);
    if (col.dataType === 'slot') {
      applySlotFill(cell, {
        isPresent: col.slotValue ? col.slotValue(row) : false,
        isAbsent: col.slotAbsentValue ? col.slotAbsentValue(row) : false,
      });
    }
  }

  if (outlineLevel > 0) {
    addedRow.outlineLevel = outlineLevel;
  }

  return addedRow.number;
}
